import './index.css'

import debounce from 'lodash/debounce'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'

import { CUSTOM_INBOX } from '@disco/constants/GroupTypes'
import {
  campaignDetailsSaveIfDirty,
  campaignDetailsUpdate,
} from '@disco/state-management/actions/campaign-details-actions'
import {
  loadMailingLists,
  loadRuleMailingLists,
} from '@disco/state-management/actions/EmailGroupActions'
import { updateBusinessSettings } from '@disco/state-management/actions/UserActions'
import {
  areRuleMailingListsLoaded,
  getCampaign,
  getCampaignRules,
} from '@disco/state-management/selectors/campaigns'
import { getMailingLists } from '@disco/state-management/selectors/emailGroups'
import {
  getCampaignSenderWithDefaults,
  getTemplateData,
} from '@disco/utils/emailCampaigns'

import type { EmailCampaign } from 'types/campaign'
import type { Group, GroupId } from 'types/group'
import type { State } from 'types/redux'
import type { User } from 'types/user'

import BusinessAddressField from 'components/common/business-address-field'
import GroupModal from 'components/common/custom-libraries/edit-form'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import { Spinner } from 'components/ui'
import AdminTool from 'components/ui/admin-tool'
import Button from 'components/ui/button'
import Label from 'components/ui/label'
import PageContainer from 'components/ui/page-container'
import TextField from 'components/ui/text-field'

import CampaignHeader from 'pages/campaigns/campaign-header'
import CampaignNav, { EMAIL_DETAILS_TAB } from 'pages/campaigns/campaign-nav'
import EditScheduledEmailModal from 'pages/campaigns/edit-scheduled-email-modal'

import type { RootState } from 'store/configureStore'

import DomainValidation from './domain-validation'
import InboxSelect from './inbox-select'

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    user: state.user,
    mailingLists: getMailingLists(state),
    mailingListsFetched: state.emailGroups.mailingListsFetched,
    ruleListsLoaded: areRuleMailingListsLoaded(state),
    recipientRules: getCampaignRules(state),
    campaign: getCampaign(state, {
      campaignId: ownProps.params.campaignId,
    }),
    modal: state.emailCampaigns.newCampaignModal,
  }
}

const mapDispatchToProps = {
  campaignDetailsUpdate,
  campaignDetailsSaveIfDirty,
  updateBusinessSettings,
  loadMailingLists,
  loadRuleMailingLists,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>
type DraftJsDoc = Record<string, any>

interface AdditionalProps {
  navigate: NavigateFunction
  params: {
    campaignId: string
  }
}

type Props = PropsFromRedux & AdditionalProps

class CampaignDetailsPage extends React.Component<
  Props,
  {
    addressSaving: boolean
    addressSaved: boolean
    addressError: Error | null | undefined
    showNewInboxModal: boolean
    business_address: DraftJsDoc | null | undefined
    business_address_mjml: string | null | undefined
  }
> {
  state = {
    addressSaving: false,
    addressSaved: false,
    addressError: null,
    showNewInboxModal: false,
    business_address: this.props.user.business.business_address,
    business_address_mjml: this.props.user.business.business_address_mjml,
  }

  componentDidMount() {
    this.props.loadMailingLists()
    this.props.loadRuleMailingLists()
  }

  componentDidUpdate(prevProps: Props) {
    // begin-edit can land after mount and swap in this campaign's rules
    if (prevProps.recipientRules.rules !== this.props.recipientRules.rules) {
      this.props.loadRuleMailingLists()
    }

    // the route swaps campaign without remounting; buffered edits must not follow
    if (prevProps.params.campaignId !== this.props.params.campaignId) {
      // this runs before campaign-master, which saves the campaign being left
      this._flushPending()
    }
  }

  componentWillUnmount() {
    this._handleBusinessAddressSave(true)
    this._saveOnExit()
  }

  _flushPending = () => {
    this._saveFields.flush()
    this._handleBusinessAddressSave.flush()
  }

  // autosave waits up to 60s, so leaving would otherwise drop the edit
  _saveOnExit = () => {
    // the flush marks the campaign dirty, which is what saveIfDirty reads
    this._flushPending()
    this.props.campaignDetailsSaveIfDirty(
      parseInt(this.props.params.campaignId),
    )
  }

  _handleInboxChange = (value: GroupId | null | undefined) => {
    const campaignId = this.props.campaign.id
    this.props.campaignDetailsUpdate(campaignId, {
      inbox: value,
    })
  }
  _handleNextStep = async () => {
    const { campaign } = this.props
    let nextStep

    switch (campaign.type) {
      case 'campaign':
        nextStep = `/campaigns/${campaign.id}/template`
        break

      case 'briefing':
        nextStep = `/campaigns/${campaign.id}/editor`
        break
    }

    if (campaign.justDuplicated) {
      nextStep = `/campaigns/${campaign.id}/editor` // override
    }

    this._flushPending()
    this.props.campaignDetailsSaveIfDirty(campaign.id)
    this.props.navigate(nextStep)
  }
  _handleCreateInbox = () => {
    this.setState({
      showNewInboxModal: true,
    })
  }
  _onCloseInboxModal = (name: string, group: Group) => {
    if (group) {
      const campaignId = this.props.campaign.id
      this.props.campaignDetailsUpdate(campaignId, {
        inbox: group.id,
      })
    }

    this.setState({
      showNewInboxModal: false,
    })
  }
  // props can point at another campaign by the time a debounced save fires
  _pendingCampaign: EmailCampaign | null = null
  _pendingFields: Partial<EmailCampaign> = {}
  _pendingExtraFields: { subject?: string } = {}
  _handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value.trim()

    switch (evt.target.name) {
      case 'campaign-subject':
        this._pendingExtraFields.subject = value
        break

      case 'campaign-name':
        this._pendingFields.name = value
        break

      case 'sender-email':
        this._pendingFields.sender_email = value
        break

      case 'sender-name':
        this._pendingFields.sender_name = value
        break
    }

    this._pendingCampaign = this.props.campaign
    this._saveFields()
  }
  // one dispatch per typing pause, not one per keystroke
  _saveFields = debounce(() => {
    const campaign = this._pendingCampaign
    const basicFields = this._pendingFields
    const extraFields = this._pendingExtraFields
    this._pendingFields = {}
    this._pendingExtraFields = {}
    if (!campaign) return
    if (!Object.keys(basicFields).length && !Object.keys(extraFields).length) {
      return
    }
    this.props.campaignDetailsUpdate(campaign.id, basicFields, extraFields)
  }, 300)
  _handleBusinessAddressChange = (val, valHtml, valMjml, valText) => {
    let normalizedVal = val
    let normalizedValMjml = valMjml

    if (!(valText || '').trim()) {
      // trim address to trigger validation at another step
      normalizedVal = null
      normalizedValMjml = ''
    }

    this.setState(
      {
        addressSaved: false,
        addressError: null,
        business_address: normalizedVal,
        business_address_mjml: normalizedValMjml,
      }, // wait for `this.setState` to flush
      // because `_handleBusinessAddressSave` will read `this.state``
      () => this._handleBusinessAddressSave(),
    )
  }
  _handleBusinessAddressSave = debounce(async (force?: boolean) => {
    if (
      !this.props.user.permissions?.includes('can_change_business_settings')
    ) {
      return // updateBusinessSettings() is rejected without that permission
    }

    if (
      !force &&
      (this.props.user.business.business_address_mjml || '') ===
        (this.state.business_address_mjml || '')
    ) {
      // up-to-date, no-op
      return
    }

    this.setState({
      addressSaving: true,
      addressError: null,
      addressSaved: false,
    })

    if (
      this.props.user.business.business_address_mjml !==
      this.state.business_address_mjml
    ) {
      try {
        await this.props.updateBusinessSettings(
          {
            business_address: this.state.business_address,
            business_address_mjml: this.state.business_address_mjml,
          },
          false,
        )
      } catch (err) {
        this.setState({
          addressSaving: false,
          addressError: err,
        })
        return
      }
    }

    this.setState({
      addressSaving: false,
      addressSaved: true,
    })
  }, 300)

  _prefillSender(): {
    sender_name: string
    sender_email: string
  } {
    return getCampaignSenderWithDefaults(
      this.props.campaign,
      this.props.user,
      this.props.mailingLists,
    )
  }

  _renderPage(children: React.ReactNode) {
    const pageTitle = 'Email details | DISCO'
    return (
      <div className="Page_Content">
        <CampaignHeader
          campaignId={this.props.params.campaignId}
          onNextClick={this._handleNextStep}
          nextDisabled={!this.props.ruleListsLoaded}
        />
        <DocumentHeadTags title={pageTitle} />
        {children}
      </div>
    )
  }

  render() {
    const { campaign } = this.props
    let inboxSelect = null

    if (!this.props.mailingListsFetched) {
      return this._renderPage(<Spinner />)
    }

    if (campaign.type === 'briefing') {
      let inboxCreateModal = null

      if (this.state.showNewInboxModal) {
        inboxCreateModal = (
          <GroupModal
            user={this.props.user}
            groupType={CUSTOM_INBOX}
            onSubmit={this._onCloseInboxModal}
            onClose={this._onCloseInboxModal}
          />
        )
      }

      inboxSelect = (
        <div className="CampaignDetails_InboxCont">
          <div className="CampaignDetails_InboxLabel">
            Select response inbox (optional)
          </div>
          <div>
            <span className="CampaignDetails_InboxInput">
              <InboxSelect
                value={campaign.inbox}
                onChange={this._handleInboxChange}
              />
            </span>
            <span className="CampaignDetails_OrSplitter">or</span>
            <Button
              caption="Create new inbox"
              onClick={this._handleCreateInbox}
            />
          </div>
          {inboxCreateModal}
        </div>
      )
    }

    const template_data = getTemplateData(campaign)

    const { sender_name, sender_email } = this._prefillSender()

    return this._renderPage(
      <PageContainer>
        <CampaignNav
          campaignId={this.props.params.campaignId}
          activeStep={EMAIL_DETAILS_TAB}
        />
        <PageContainer.Section>
          <Row columnsSize={[6, 6]}>
            <div>
              <TextField
                label="Email name"
                name="campaign-name"
                defaultValue={campaign.name}
                onChange={this._handleChange}
              />
            </div>
            <div>
              <TextField
                label="Email subject"
                name="campaign-subject"
                defaultValue={template_data.subject}
                onChange={this._handleChange}
              />
            </div>
          </Row>
          <Row columnsSize={[6, 6]}>
            <div>
              <TextField
                label="Sender from address"
                name="sender-email"
                defaultValue={sender_email}
                onChange={this._handleChange}
              />
            </div>
            <div>
              <TextField
                label="Sender display name"
                name="sender-name"
                defaultValue={sender_name}
                onChange={this._handleChange}
              />
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <label className="Form_Label">
              <Label
                caption={
                  'Business address' +
                  (this.state.addressSaving ? ' (updating)' : '') +
                  (this.state.addressSaved ? ' (updated!)' : '')
                }
                validationError={
                  this.state.addressError && this.state.addressError.toString()
                }
              />
              <BusinessAddressField
                invalid={Boolean(this.state.addressError)}
                initialContent={this.state.business_address}
                onChange={this._handleBusinessAddressChange}
              />
            </label>
          </Row>

          <AdminTool
            renderFn={() => <DomainValidation email={sender_email} />}
          />

          {inboxSelect && (
            <div className="CampaignDetails_OptionalRow">
              <div className="CampaignDetails_InputWrap">{inboxSelect}</div>
            </div>
          )}
        </PageContainer.Section>
        <EditScheduledEmailModal campaignId={this.props.params.campaignId} />
      </PageContainer>,
    )
  }
}

const ConnectedCampaignDetailsPage = connector(CampaignDetailsPage)

function CampaignDetailsPageWithRouter(props: any) {
  const navigate = useNavigate()
  const params = useParams<{ campaignId: string }>()
  return (
    <ConnectedCampaignDetailsPage
      {...props}
      navigate={navigate}
      params={params}
    />
  )
}

export default CampaignDetailsPageWithRouter
